// B-93 — the Alertas feed as an Excel download, for the date range and filters on screen.
//
// Same two halves as `cierreExcel.js`: `hojaDeAlertas` decides every cell from the feed's rows with
// no library, and is what the tests pin; `libroDeAlertas` hands that to exceljs, which is loaded
// only when somebody actually downloads. The rows arrive already filtered and in the screen's
// order — this file writes what it is given and decides nothing about which alerts go in.

import { BORDES, letraDeColumna, relleno } from "./cierreExcel";
import { STATUS_LABELS } from "./cierreCorrections";
import { mensajeDeAlerta, nombreDeAlerta, nombreDeFuente } from "./cierreNombres";
import {
  AUTOR_DEL_SISTEMA, ESTADO_POR_CORRECCION, ajusteDeAlerta, esAlertaAbierta, fechaHoraBogota, observacionDeAlerta,
} from "./cierrePanel";
import { parseDateOnly } from "./dateUtils";

const PRIORIDAD = { alta: "Alta", media: "Media", baja: "Baja" };
const RELLENO_ENCABEZADO = "D9E1F2";

const COLUMNAS = [
  { header: "Fecha", width: 12, numFmt: "dd/mm/yyyy" },
  { header: "Código oficina", width: 14 },
  { header: "Oficina", width: 32 },
  { header: "Región", width: 18 },
  // B-118 — Prioridad, Mensaje and the amount side by side: the priority is graded from the amount,
  // and the message says what the amount is (caja or bóveda, the sign as written there).
  { header: "Alerta", width: 30 },
  { header: "Prioridad", width: 11 },
  { header: "Mensaje", width: 60, wrap: true },
  { header: "Monto del descuadre", width: 20, numFmt: '"$"#,##0.00;-"$"#,##0.00' },
  { header: "Documentos", width: 32, wrap: true },
  { header: "Estado", width: 24 },
  { header: "Atendida por", width: 24 },
  { header: "Fecha de atención", width: 20 },
  { header: "Observación", width: 50, wrap: true },
];

// `YYYY-MM-DD` as an Excel date — built in UTC, so no browser zone can move it to the day before.
function fechaExcel(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

// An office code as Excel stores it: a number when it is one (201), the text otherwise.
function codigoExcel(codigo) {
  if (codigo === null || codigo === undefined || codigo === "") return null;
  return /^\d+$/.test(String(codigo)) ? Number(codigo) : String(codigo);
}

// B-118 — the amount the alert is about as a number Excel can filter by range, signed as the message
// writes it. The feed serves it as a decimal string; none (a "Falta…" alert) is an empty cell.
function montoExcel(monto) {
  if (monto === null || monto === undefined || monto === "") return null;
  const n = Number(monto);
  return Number.isFinite(n) ? n : null;
}

// Absent is an empty cell — never "—", which is how the screen draws one.
const texto = (valor) => (valor === null || valor === undefined || valor === "" || valor === "—" ? null : valor);

function filaDeAlerta(a, nombres) {
  const abierta = esAlertaAbierta(a);
  const documentos = (a.documentos || []).map((d) => nombreDeFuente(nombres, d.sourceKey)).filter(Boolean);
  return [
    fechaExcel(a.fechaISO),
    codigoExcel(a.oficinaCodigo),
    texto(a.oficina),
    texto(a.region),
    a.ruleKey ? nombreDeAlerta(nombres, a.ruleKey) : null,
    PRIORIDAD[a.prioridad] || null,
    texto(mensajeDeAlerta(nombres, a.titulo)),
    montoExcel(a.monto),
    documentos.length ? documentos.join(", ") : null,
    // B-116 — one a person's correction resolved says so, with who corrected, when and why.
    texto(a.correctedBy ? ESTADO_POR_CORRECCION : STATUS_LABELS[a.status] || a.status),
    // The engine signs its own resolutions; "evaluation" is a key, not somebody who attended it.
    a.correctedBy || (!abierta && a.resolvedBy && a.resolvedBy !== AUTOR_DEL_SISTEMA ? a.resolvedBy : null),
    resueltaEn(a, abierta),
    // B-115/B-116 — the observation (a correction's reason, else the note), and beside it the
    // adjustment an attention made to the next day.
    texto([observacionDeAlerta(a), ajusteDeAlerta(a)].filter(Boolean).join(" · ")),
  ];
}

function resueltaEn(a, abierta) {
  const cuando = a.correctedAt || (!abierta ? a.resolvedAt : null);
  return cuando ? texto(fechaHoraBogota(cuando)) : null;
}

// `YYYY-MM-DD` as `DD-MM-YY`. Dashes, not slashes: a `/` is not allowed in a file name.
function fechaCorta(iso) {
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y.slice(-2)}`;
}

// "Alertas <cliente> 01-10-26 a 16-10-26.xlsx" — the range asked for. An open end (the "Todo"
// chip, or a custom range with one side empty) takes the oldest or newest day among the rows.
function nombreDeArchivo(clientCode, rango, alertas) {
  const base = clientCode ? `Alertas ${clientCode}` : "Alertas";
  const dias = alertas.map((a) => a.fechaISO).filter(Boolean).sort();
  const desde = parseDateOnly(rango?.desde) || dias[0];
  const hasta = parseDateOnly(rango?.hasta) || dias[dias.length - 1];
  if (!desde || !hasta) return `${base}.xlsx`;
  return desde === hasta ? `${base} ${fechaCorta(desde)}.xlsx` : `${base} ${fechaCorta(desde)} a ${fechaCorta(hasta)}.xlsx`;
}

/**
 * Every cell of the download, decided without a library.
 *
 * @param {object}   options
 * @param {object[]} options.alertas    the feed's rows (CierresAlertas), in the order to write them
 * @param {?object}  options.nombres    the client's readable names (`useNombresCierre`)
 * @param {?object}  options.rango      the effective date range `{ desde, hasta }`, both optional
 * @param {?string}  options.clientCode the client, for the file name
 */
export function hojaDeAlertas({ alertas = [], nombres = null, rango = null, clientCode = null }) {
  return {
    hoja: "Alertas",
    archivo: nombreDeArchivo(clientCode, rango, alertas),
    fuente: { name: "Calibri", size: 11 },
    columnas: COLUMNAS,
    filas: alertas.map((a) => filaDeAlerta(a, nombres)),
  };
}

/**
 * The sheet as an exceljs workbook. `ExcelJS` is passed in, so a test hands it the real library
 * and the screen loads it only on a click.
 */
export function libroDeAlertas(ExcelJS, hoja) {
  const libro = new ExcelJS.Workbook();
  const ws = libro.addWorksheet(hoja.hoja, { views: [{ state: "frozen", ySplit: 1 }] });

  hoja.columnas.forEach((c, i) => {
    ws.getColumn(i + 1).width = c.width;
  });

  const encabezado = ws.getRow(1);
  hoja.columnas.forEach((c, i) => {
    const celda = encabezado.getCell(i + 1);
    celda.value = c.header;
    celda.font = { ...hoja.fuente, bold: true };
    celda.alignment = { vertical: "middle" };
    celda.border = BORDES;
    celda.fill = relleno(RELLENO_ENCABEZADO);
  });

  hoja.filas.forEach((valores, r) => {
    const fila = ws.getRow(r + 2);
    valores.forEach((valor, i) => {
      const c = hoja.columnas[i];
      const celda = fila.getCell(i + 1);
      celda.value = valor;
      celda.font = hoja.fuente;
      celda.border = BORDES;
      celda.alignment = { vertical: "top", wrapText: !!c.wrap };
      if (c.numFmt) celda.numFmt = c.numFmt;
    });
  });

  ws.autoFilter = `A1:${letraDeColumna(hoja.columnas.length)}${Math.max(1, hoja.filas.length + 1)}`;
  return libro;
}

// Build and hand the browser the file. exceljs is loaded here, on the click, not with the screen.
export async function descargarAlertas(hoja) {
  const [{ default: ExcelJS }, { saveAs }] = await Promise.all([import("exceljs"), import("file-saver")]);
  const buffer = await libroDeAlertas(ExcelJS, hoja).xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    hoja.archivo
  );
}
